'use client'

import * as React from 'react'

import { cn } from '@repo/ui/lib/utils'
import { Popover } from '../Popover'
import { Separator } from '../Separator'
import {
  buildYearRange,
  clampDate,
  DEFAULT_YEAR_END,
  DEFAULT_YEAR_START,
  gridCellClass,
  gridKeyDownHandler,
  MONTH_INDICES,
} from './constants'
import {
  PickerHeader,
  StepperFrame,
  StepperPopoverContent,
  StepperValueButton,
  YearListSection,
} from './shared'

type MonthPickerLabels = {
  /** aria-label for the button showing the selected month and year. */
  monthAndYear: string
  previousMonth: string
  nextMonth: string
  previousYear: string
  nextYear: string
  chooseYear: string
}

type MonthPickerProps = {
  /**
   * Only the year and month are read; commits are always the first day of the month.
   * Undefined shows the placeholder, and interactions step from the current month.
   */
  value?: Date
  onValueChange: (next: Date) => void
  /** Shown while `value` is undefined. Supplied by the caller — this package has no i18n of its own. */
  placeholder?: string
  /** BCP-47 tag. Supplied by the caller — this package has no locale of its own. */
  locale: string
  /** Labels are supplied by the caller — this package has no i18n of its own. */
  labels: MonthPickerLabels
  /** Forwarded to the value button, so a visible `<label htmlFor>` can target it. */
  id?: string
  /** Hides the leading calendar icon shown before the value. */
  hideCalendarIcon?: boolean
  /** Earliest selectable month; only its year and month are read. */
  minDate?: Date
  /** Latest selectable month; only its year and month are read. */
  maxDate?: Date
  disabled?: boolean
  className?: string
  /** Applied to the visible control (chevrons + value), e.g. to size it to its neighbours. */
  frameClassName?: string
  /**
   * Applied to the value button, e.g. a fixed width so the control doesn't resize as the label
   * changes. A label that doesn't fit is truncated from the end.
   */
  valueClassName?: string
  /**
   * Rendered under the month grid, behind a divider, for an action of the caller's (e.g. jump to
   * the current period). Given `close` so the action can dismiss the popover like a pick would.
   */
  footer?: (helpers: { close: () => void }) => React.ReactNode
  /**
   * Rendered beside the month grid, behind a divider, for a second pick that belongs with the
   * month (e.g. a version within the period). The popover widens to fit it, but the month column
   * alone sets its height: the aside is clipped to it, so a long aside must scroll itself. Given
   * `close`, like `footer`.
   */
  aside?: (helpers: { close: () => void }) => React.ReactNode
  /**
   * Rendered under the aside, beside `footer`, for an action that belongs with the aside's pick. With an
   * aside, `footer` keeps to the month column and this to the aside's, so each lines up with its section.
   */
  asideFooter?: (helpers: { close: () => void }) => React.ReactNode
  /**
   * Shown after the month and year in the value button, behind a separator, for a selection the
   * footer completes (e.g. a version within the period). The button then sizes to its content.
   */
  labelSuffix?: React.ReactNode
  /**
   * Whether picking a month closes the popover (default). Pass false when `aside` or `footer` holds
   * a second pick that depends on the month, so the user can make both without reopening.
   */
  shouldCloseOnSelect?: boolean
}

/**
 * A month stepper whose value can also be picked from a month grid with a
 * year list, under the control.
 */
function MonthPicker({
  value,
  onValueChange,
  placeholder,
  locale,
  labels,
  id,
  hideCalendarIcon,
  minDate,
  maxDate,
  disabled,
  className,
  frameClassName,
  valueClassName,
  footer,
  aside,
  asideFooter,
  labelSuffix,
  shouldCloseOnSelect = true,
}: MonthPickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [viewYear, setViewYear] = React.useState(() => (value ?? new Date()).getFullYear())
  const [isYearListOpen, setIsYearListOpen] = React.useState(false)

  // A value changed from outside while open (e.g. a footer action) brings the grid to its year,
  // so the new selection is visible rather than left on whichever year was being browsed.
  const valueYear = value?.getFullYear()
  const [syncedValueYear, setSyncedValueYear] = React.useState(valueYear)
  if (valueYear !== syncedValueYear) {
    setSyncedValueYear(valueYear)
    if (valueYear !== undefined) setViewYear(valueYear)
  }

  const rootRef = React.useRef<HTMLDivElement>(null)
  const valueButtonRef = React.useRef<HTMLButtonElement>(null)
  const popoverId = React.useId()

  // Bounds normalized to the first of their month, the granularity every value here has.
  const minMonth = minDate
    ? new Date(minDate.getFullYear(), minDate.getMonth(), 1)
    : new Date(DEFAULT_YEAR_START, 0, 1)
  const maxMonth = maxDate
    ? new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)
    : new Date(DEFAULT_YEAR_END, 11, 1)

  const isMonthInRange = (candidate: Date) =>
    candidate.getTime() >= minMonth.getTime() && candidate.getTime() <= maxMonth.getTime()

  const valueMonth = value ? new Date(value.getFullYear(), value.getMonth(), 1) : undefined
  // What stepping and the popover open on: the value, or the current month while there is
  // none — always clamped into range, so an out-of-range value can't strand the steppers.
  const today = new Date()
  const baseMonth = clampDate(
    valueMonth ?? new Date(today.getFullYear(), today.getMonth(), 1),
    minMonth,
    maxMonth,
  )

  const monthFormat = new Intl.DateTimeFormat(locale, { month: 'short' })

  const monthLabel = value ? (
    new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(value)
  ) : (
    <span className="text-muted-foreground">{placeholder}</span>
  )

  const label =
    labelSuffix != null ? (
      <>
        {monthLabel}
        <span className="text-muted-foreground"> · </span>
        {labelSuffix}
      </>
    ) : (
      monthLabel
    )

  const applyChange = (next: Date) => {
    setViewYear(next.getFullYear())
    onValueChange(next)
  }

  const commit = (nextMonth: number, nextYear: number) => {
    const next = clampDate(new Date(nextYear, nextMonth, 1), minMonth, maxMonth)
    setViewYear(next.getFullYear())
    if (shouldCloseOnSelect) setIsOpen(false)
    onValueChange(next)
  }

  // Steps clamp into range rather than no-op, so a step from an out-of-range start (an empty
  // value clamped to a bound, or a controlled value outside the range) still commits a month.
  const stepMonth = (delta: number) => {
    const from = valueMonth ?? baseMonth
    const next = new Date(from.getFullYear(), from.getMonth() + delta, 1)
    applyChange(clampDate(next, minMonth, maxMonth))
  }

  // Navigating years only changes the visible grid, not the selection — a browse gesture,
  // like DayPicker's month chevrons. A year is reachable as long as it touches the range.
  const navigateYear = (delta: number) => {
    const nextYear = viewYear + delta
    if (nextYear < minMonth.getFullYear() || nextYear > maxMonth.getFullYear()) return
    setViewYear(nextYear)
  }

  // Picking a year from the list commits immediately, keeping the selected month (clamped
  // into range).
  const applyYear = (nextYear: number) => {
    applyChange(clampDate(new Date(nextYear, baseMonth.getMonth(), 1), minMonth, maxMonth))
  }

  const toggleOpen = () => {
    if (isOpen) {
      setIsOpen(false)
      return
    }
    setViewYear(baseMonth.getFullYear())
    setIsYearListOpen(false)
    setIsOpen(true)
  }

  const highlightedMonth =
    value !== undefined && viewYear === value.getFullYear() ? value.getMonth() : -1
  // The grid's roving tab stop: the selected month, or the first enabled cell when the
  // viewed year isn't the value's.
  const tabStopMonth =
    highlightedMonth >= 0
      ? highlightedMonth
      : MONTH_INDICES.find((monthIndex) => isMonthInRange(new Date(viewYear, monthIndex, 1)))

  return (
    <div ref={rootRef} className={cn('relative inline-block', className)}>
      <Popover open={isOpen} onOpenChange={(nextOpen) => !nextOpen && setIsOpen(false)}>
        <StepperFrame
          stepBackLabel={labels.previousMonth}
          stepForwardLabel={labels.nextMonth}
          onStepBack={() => stepMonth(-1)}
          onStepForward={() => stepMonth(1)}
          stepBackDisabled={valueMonth !== undefined && valueMonth.getTime() <= minMonth.getTime()}
          stepForwardDisabled={
            valueMonth !== undefined && valueMonth.getTime() >= maxMonth.getTime()
          }
          disabled={disabled}
          className={frameClassName}
        >
          <StepperValueButton
            id={id}
            isOpen={isOpen}
            onToggle={toggleOpen}
            disabled={disabled}
            ariaLabel={labels.monthAndYear}
            hideCalendarIcon={hideCalendarIcon}
            widthClass={cn(
              labelSuffix != null ? 'px-1' : hideCalendarIcon ? 'w-[12ch]' : 'w-[15ch]',
              valueClassName,
            )}
            popoverId={popoverId}
            buttonRef={valueButtonRef}
          >
            {label}
          </StepperValueButton>
        </StepperFrame>

        <StepperPopoverContent
          rootRef={rootRef}
          id={popoverId}
          returnFocusRef={valueButtonRef}
          className={aside ? 'w-auto' : undefined}
        >
          <div className="flex">
            <div className="w-[220px] shrink-0">
              <PickerHeader
                label={String(viewYear)}
                chooseYearLabel={labels.chooseYear}
                isYearListOpen={isYearListOpen}
                onToggleYearList={() => setIsYearListOpen((isListOpen) => !isListOpen)}
                stepBackLabel={labels.previousYear}
                stepForwardLabel={labels.nextYear}
                onStepBack={() => navigateYear(-1)}
                onStepForward={() => navigateYear(1)}
                stepBackDisabled={viewYear <= minMonth.getFullYear()}
                stepForwardDisabled={viewYear >= maxMonth.getFullYear()}
              />

              {isYearListOpen ? (
                <YearListSection
                  years={buildYearRange(minMonth.getFullYear(), maxMonth.getFullYear())}
                  selectedYear={value?.getFullYear()}
                  tabStopYear={viewYear}
                  visibleRows={4}
                  onSelectYear={(nextYear) => {
                    applyYear(nextYear)
                    setIsYearListOpen(false)
                  }}
                />
              ) : (
                <div
                  onKeyDown={gridKeyDownHandler(3)}
                  className="grid grid-cols-3 gap-x-3 gap-y-3 px-3 py-3"
                >
                  {MONTH_INDICES.map((monthIndex) => (
                    <button
                      key={monthIndex}
                      type="button"
                      tabIndex={monthIndex === tabStopMonth ? 0 : -1}
                      aria-pressed={monthIndex === highlightedMonth}
                      onClick={() => commit(monthIndex, viewYear)}
                      disabled={!isMonthInRange(new Date(viewYear, monthIndex, 1))}
                      className={cn(
                        'py-1 transition-colors',
                        gridCellClass(monthIndex === highlightedMonth),
                      )}
                    >
                      {monthFormat.format(new Date(2000, monthIndex, 1))}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {aside && (
              <>
                <Separator orientation="vertical" className="my-0 h-auto self-stretch" />
                {/* Out of flow so it never makes the row taller than the month column. */}
                <div className="relative w-60">
                  <div className="absolute inset-0 overflow-hidden p-2">
                    {aside({ close: () => setIsOpen(false) })}
                  </div>
                </div>
              </>
            )}
          </div>

          {(footer || (aside && asideFooter)) && (
            <>
              <Separator />
              {aside ? (
                // The columns' own widths, so each footer lines up with the section above it.
                <div className="flex">
                  <div className="w-[220px] shrink-0 p-2">
                    {footer?.({ close: () => setIsOpen(false) })}
                  </div>
                  <div className="w-px shrink-0" />
                  <div className="w-60 p-2">{asideFooter?.({ close: () => setIsOpen(false) })}</div>
                </div>
              ) : (
                <div className="p-2">{footer?.({ close: () => setIsOpen(false) })}</div>
              )}
            </>
          )}
        </StepperPopoverContent>
      </Popover>
    </div>
  )
}

export { MonthPicker }
export type { MonthPickerLabels, MonthPickerProps }
